import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Package, Folder, FileText, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/user-store';
import { useFileStore } from '../../stores/file-store';
import { useCategoryStore } from '../../stores/category-store';
import { supabase } from '../../config/supabase';
import { useToast } from '../shared/ToastContainer';
import SearchModal from './SearchModal';

interface MobileSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// 搜索建议类型定义
interface SearchSuggestion {
  id: string;
  type: 'order' | 'folder' | 'file';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  data: any;
}

// 订单搜索结果
interface OrderResult {
  id: string;
  order_number: string;
  description?: string;
  folder_name: string;
  file_count?: number;
}

const MobileSearchModal: React.FC<MobileSearchModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useUserStore();
  const { categories } = useCategoryStore();
  
  // 智能搜索状态
  const [smartSearchQuery, setSmartSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResultType, setSearchResultType] = useState<'file' | 'order' | 'folder' | null>(null);
  const [showFileSearchModal, setShowFileSearchModal] = useState(false);
  
  // 搜索相关引用
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 自动聚焦到搜索框
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // 延迟聚焦，避免动画冲突
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // 智能搜索功能实现
  const generateSearchSuggestions = useCallback(async (query: string): Promise<SearchSuggestion[]> => {
    if (!query.trim()) return [];
    
    const suggestions: SearchSuggestion[] = [];
    const lowercaseQuery = query.toLowerCase().trim();
    
    try {
      // 1. 订单号搜索 (最高优先级)
      const { data: orders } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          description,
          folder_name,
          order_files(count)
        `)
        .or(`order_number.ilike.%${lowercaseQuery}%,folder_name.ilike.%${lowercaseQuery}%`)
        .eq('is_active', true)
        .limit(3);
      
      if (orders?.length) {
        orders.forEach(order => {
          suggestions.push({
            id: `order-${order.id}`,
            type: 'order',
            title: `📋 订单: ${order.order_number}`,
            subtitle: order.description || `自动创建的订单 - ${order.folder_name}`,
            icon: <Package size={16} className="text-blue-600" />,
            data: order
          });
        });
      }
      
      // 2. 文件夹搜索 (次优先级)
      const folderSuggestions = categories
        .filter(category => 
          category.name.toLowerCase().includes(lowercaseQuery)
        )
        .slice(0, 3)
        .map(category => ({
          id: `folder-${category.id}`,
          type: 'folder' as const,
          title: `📁 文件夹: ${category.name}`,
          subtitle: `直接跳转到此分类`,
          icon: <Folder size={16} className="text-amber-600" />,
          data: category
        }));
      
      suggestions.push(...folderSuggestions);
      
      // 3. 文件搜索 (兜底方案)
      if (suggestions.length < 5) {
        suggestions.push({
          id: `file-search-${query}`,
          type: 'file',
          title: `📄 搜索文件: "${query}"`,
          subtitle: '打开高级搜索，在所有文件中查找',
          icon: <FileText size={16} className="text-gray-600" />,
          data: { query }
        });
      }
      
    } catch (error) {
      console.error('搜索建议生成失败:', error);
    }
    
    return suggestions.slice(0, 6);
  }, [categories]);
  
  // 防抖搜索
  const debouncedSearch = useCallback((query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      if (query.trim()) {
        setIsSearching(true);
        const suggestions = await generateSearchSuggestions(query);
        setSearchSuggestions(suggestions);
        setShowSuggestions(true);
        setIsSearching(false);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        setSearchResultType(null);
      }
    }, 400);
  }, [generateSearchSuggestions]);
  
  // 处理搜索输入变化
  const handleSmartSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSmartSearchQuery(query);
    debouncedSearch(query);
  };

  // 处理搜索建议选择
  const handleSuggestionSelect = async (suggestion: SearchSuggestion) => {
    setSmartSearchQuery(suggestion.title);
    setShowSuggestions(false);
    setSearchResultType(suggestion.type);
    
    switch (suggestion.type) {
      case 'order':
        try {
          const order = suggestion.data as OrderResult;
          
          // 记录用户访问
          if (profile?.id) {
            await supabase
              .from('order_user_access')
              .upsert({
                order_id: order.id,
                user_id: profile.id,
                accessed_at: new Date().toISOString()
              });
          }
          
          // 查找或创建关联的文件夹
          let categoryId: string | null = null;
          
          const { data: existingBinding } = await supabase
            .from('category_orders')
            .select('category_id, categories(id, name)')
            .eq('order_id', order.id)
            .single();
          
          if (existingBinding?.category_id) {
            categoryId = existingBinding.category_id;
          } else {
            const { data: existingCategory } = await supabase
              .from('categories')
              .select('id')
              .eq('name', order.folder_name)
              .single();
              
            if (existingCategory) {
              categoryId = existingCategory.id;
              
              await supabase
                .from('category_orders')
                .insert({
                  category_id: categoryId,
                  order_id: order.id,
                  binding_type: 'auto'
                });
            } else {
              try {
                const newCategory = await useCategoryStore.getState().createCategory(
                  order.folder_name,
                  undefined,
                  { 
                    isOrderFolder: true, 
                    orderNumber: order.order_number,
                    isPublic: true 
                  }
                );
                categoryId = newCategory.id;
                
                await supabase
                  .from('category_orders')
                  .insert({
                    category_id: categoryId,
                    order_id: order.id,
                    binding_type: 'auto'
                  });
              } catch (createError) {
                console.error('创建文件夹失败:', createError);
              }
            }
          }
          
          // 关闭模态框并跳转
          if (categoryId) {
            onClose();
            setTimeout(() => {
              navigate(`/uadmin/categories/${categoryId}`);
            }, 300);
            toast.addToast(`✅ 订单 ${order.order_number} 查询成功！正在跳转...`, 'success', 1500);
          } else {
            toast.addToast(`⚠️ 订单 ${order.order_number} 查询成功！但未找到关联文件夹`, 'warning', 3000);
          }
          
        } catch (error) {
          console.error('订单查询失败:', error);
          toast.addToast('订单查询失败，请重试', 'error', 3000);
        }
        break;
        
      case 'folder':
        try {
          const category = suggestion.data;
          onClose();
          setTimeout(() => {
            navigate(`/uadmin/categories/${category.id}`);
          }, 300);
          toast.addToast(`🚀 正在跳转到文件夹: ${category.name}`, 'success', 1500);
        } catch (error) {
          console.error('文件夹跳转失败:', error);
          toast.addToast('文件夹跳转失败，请重试', 'error', 3000);
        }
        break;
        
      case 'file':
        // 关闭当前模态框，打开文件搜索模态框
        onClose();
        setShowFileSearchModal(true);
        // 预填搜索词
        useFileStore.getState().setSearchQuery(suggestion.data.query);
        break;
    }
  };
  
  // 键盘事件处理
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && searchSuggestions.length > 0) {
      handleSuggestionSelect(searchSuggestions[0]);
    }
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {/* 模态框背景遮罩 */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
        onClick={onClose}
      />
      
      {/* 移动端搜索模态框 */}
      <div className="fixed inset-x-0 top-0 z-50 md:hidden">
        <div className="bg-white/95 backdrop-blur-xl border-b border-gray-200/80 shadow-lg">
          {/* 搜索头部 */}
          <div className="flex items-center px-4 py-3">
            <div className="relative flex-1">
              <div className="flex items-center h-11 px-4 text-base transition-all bg-gray-50/80 backdrop-blur-xl border border-gray-200/80 rounded-full focus-within:ring-2 focus-within:ring-teal-500/50 focus-within:border-teal-500/50 shadow-sm">
                <Search size={20} className={cn("mr-3 transition-colors", isSearching ? "text-teal-500 animate-pulse" : "text-gray-400")} />
                
                <input
                  ref={searchInputRef}
                  type="text"
                  value={smartSearchQuery}
                  onChange={handleSmartSearchChange}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent border-none focus:outline-none text-gray-800 placeholder-gray-500 text-base"
                  placeholder="智能搜索订单、文件夹、文件..."
                />
                
                {/* 搜索状态指示器 */}
                {searchResultType && (
                  <div className="flex items-center ml-2">
                    {searchResultType === 'order' && <Package size={16} className="text-blue-600" />}
                    {searchResultType === 'folder' && <Folder size={16} className="text-amber-600" />}
                    {searchResultType === 'file' && <FileText size={16} className="text-gray-600" />}
                  </div>
                )}
                
                {/* 清除按钮 */}
                {smartSearchQuery && (
                  <button
                    onClick={() => {
                      setSmartSearchQuery('');
                      setShowSuggestions(false);
                      setSearchResultType(null);
                      searchInputRef.current?.focus();
                    }}
                    className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            
            {/* 关闭按钮 */}
            <button
              onClick={onClose}
              className="ml-3 p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>
          
          {/* 搜索建议区域 */}
          {(showSuggestions && searchSuggestions.length > 0) || isSearching ? (
            <div className="border-t border-gray-100/80 bg-white/90 backdrop-blur-xl max-h-96 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-base text-gray-600">智能搜索中...</span>
                </div>
              ) : (
                <div className="py-2">
                  {searchSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      onClick={() => handleSuggestionSelect(suggestion)}
                      className="w-full px-4 py-4 flex items-center hover:bg-gray-50/80 transition-colors text-left group active:bg-gray-100"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100/80 mr-4 group-hover:scale-105 transition-transform">
                        {suggestion.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 text-base truncate">
                          {suggestion.title}
                        </div>
                        {suggestion.subtitle && (
                          <div className="text-sm text-gray-500 truncate mt-0.5">
                            {suggestion.subtitle}
                          </div>
                        )}
                      </div>
                      <ChevronRight size={18} className="text-gray-300 group-hover:text-gray-500 transition-colors ml-2" />
                    </button>
                  ))}
                  
                  {/* 搜索提示 */}
                  <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500 bg-gray-50/30">
                    <div className="flex items-center justify-between">
                      <span>回车搜索 • 轻触选择</span>
                      <span className="text-teal-600 font-medium">按优先级排序</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : smartSearchQuery && !isSearching ? (
            <div className="border-t border-gray-100/80 bg-white/90 backdrop-blur-xl py-8">
              <div className="text-center text-gray-500">
                <Search size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-base">未找到相关结果</p>
                <p className="text-sm mt-1">试试其他关键词</p>
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-100/80 bg-white/90 backdrop-blur-xl py-6">
              <div className="text-center text-gray-400">
                <Search size={28} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">输入关键词开始搜索</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 传统文件搜索模态框 */}
      {showFileSearchModal && (
        <SearchModal onClose={() => setShowFileSearchModal(false)} />
      )}
    </>
  );
};

export default MobileSearchModal;
